import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AccessDenied from './AccessDenied';

function isAdminLike({ area, can }) {
  // "admin area" must be determined by role/area only (not permissions),
  // otherwise Manager-like roles with many permissions get forced into admin routes.
  return area === 'admin';
}

function dashboardHomePath(area) {
  if (area === 'admin') return '/dashboard/admin';
  if (area === 'backend') return '/dashboard/backend';
  return '/dashboard/employee';
}

function defaultDashboardPath({ area, can, canBehaviourTrack }) {
  const adminLike = isAdminLike({ area, can });
  if (area === 'backend') {
    if (can?.('dashboard')) return '/dashboard/backend';
    if (can?.('complaints')) return '/dashboard/backend/complaints';
    if (can?.('leave_management')) return '/dashboard/backend/leave-management';
    return '/landing';
  }
  // Pick first accessible destination (permission-first).
  if (can?.('dashboard')) return adminLike ? '/dashboard/admin' : '/dashboard/employee';
  if (can?.('complaints'))
    return adminLike ? '/dashboard/admin/complaints' : '/dashboard/employee/complaints';
  if (can?.('leave_management'))
    return adminLike ? '/dashboard/admin/leave-management' : '/dashboard/employee/leave-management';
  if (can?.('categories')) return '/dashboard/admin/categories';
  if (can?.('product_category')) return '/dashboard/admin/product-category';
  if (can?.('employees')) return '/dashboard/admin/employees';
  if (can?.('outsource_employee')) return '/dashboard/admin/outsource-employee';
  if (can?.('issue_types')) return '/dashboard/admin/issue-type';
  if (can?.('outlets')) return '/dashboard/admin/outlets';
  if (can?.('products')) return '/dashboard/admin/products';
  if (can?.('licences')) return '/dashboard/admin/licences';
  if (canBehaviourTrack) return '/dashboard/admin/employees';
  if (can?.('behaviour_tracking')) return '/dashboard/admin/behaviour-tracking';
  return '/landing';
}

export function ProtectedRoute({
  children,
  requiredPermission,
  requiredPermissions,
  requiredMenuAction,
  requiredRole,
  requiredRoles,
}) {
  const { isAuthenticated, user, can, canAction, area, roleKey, canBehaviourTrack } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const roles = requiredRoles || (requiredRole ? [requiredRole] : null);
  if (roles && roles.length > 0) {
    const currentRole = roleKey || user?.roleKey;
    const ok = roles.some((r) => String(r).toLowerCase() === String(currentRole).toLowerCase());
    if (!ok) {
      const fallback =
        String(currentRole || '').toLowerCase() === 'backend'
          ? '/dashboard/backend'
          : defaultDashboardPath({ area, can, canBehaviourTrack, user });
      return <Navigate to={fallback} replace />;
    }
  }


  const isAdminDashboardHome =
    location.pathname === '/dashboard/admin' || location.pathname === '/dashboard/admin/';
  if (isAdminDashboardHome && area !== 'admin') {
    return <Navigate to={dashboardHomePath(area)} replace />;
  }

  const perms = requiredPermissions || (requiredPermission ? [requiredPermission] : null);
  if (perms && perms.length > 0) {
    const ok = perms.every(
      (p) => can(p) || (p === 'behaviour_tracking' && canBehaviourTrack)
    );
    if (!ok) return <AccessDenied />;
  }

  if (requiredMenuAction && requiredPermission) {
    const actionOk = canAction(requiredPermission, requiredMenuAction);
    const behaviourTrackBypass =
      requiredPermission === 'behaviour_tracking' &&
      canBehaviourTrack &&
      ['view', 'create', 'edit'].includes(String(requiredMenuAction).toLowerCase());
    if (!actionOk && !behaviourTrackBypass) {
      return <AccessDenied />;
    }
  }

  return children;
}
